 
import { put, takeLatest, select, takeEvery } from 'redux-saga/effects';
import uniqBy from 'lodash/uniqBy';

import CreativeServices from 'services/CreativeServices';
import types from 'redux/types';
import hooks from 'hooks';
import { pageSize } from 'consts/filters';
import { TIME_OUT_CLOSE_TOAST } from 'consts/data';
import { notifySuccess } from 'helpers/handleNotification';
import { getNextDataRefetch } from 'helpers';
import { AppRoutes } from 'consts/routesPath';
import cloneDeep from 'lodash/cloneDeep';
import { showError } from 'helpers/handleError';

const { Notification } = hooks;

function* getCreativeById(data) {
  const { id, callbacks } = data;
  try {
    const res = yield CreativeServices.getById({ id });
    if (res?.data?.code === 400) {
      showError(res?.data?.message);
      yield put({ type: types.REQUEST_GET_CREATIVE_BY_ID_FAILED, error: res?.data?.message });
      callbacks && callbacks.onFailed(res?.data?.message);
    } else {
      yield put({ type: types.REQUEST_GET_CREATIVE_BY_ID_SUCCESS, creative: res.data });
      callbacks && callbacks.onSuccess(res);
    }
  } catch (error) {
    showError(error);
    yield put({
      type: types.REQUEST_GET_CREATIVE_BY_ID_FAILED,
      error: error.response.statusText || 'Something went wrong!',
    });
    callbacks && callbacks.onFailed(error);
  }
}

function* getListCreative() {
  try {
    const filters = yield select((state) => state.filterReducer.filterCreative);

    const res = yield CreativeServices.getList(filters);
    const isLast = res.data.data.length < pageSize;
    yield put({ type: types.REQUEST_GET_LIST_CREATIVE_SUCCESS, data: res.data.data, isLast });
  } catch (error) {
    showError(error);
    yield put({ type: types.REQUEST_GET_LIST_CREATIVE_FAILED, error });
  }
}

function* refetchListCreative() {
  try {
    const filters = yield select((state) => state.filterReducer.filterCreative);

    // list Request
    let listRequest = [];
    for (let i = 0; i < filters.page; i++) {
      const newOffset = i * pageSize;
      listRequest = [...listRequest, CreativeServices.getList({ ...filters, offset: newOffset })];
    }

    const response = yield Promise.all(listRequest);
    const [nextData, isLast] = getNextDataRefetch(response, pageSize, 'data');
    yield put({ type: types.REQUEST_GET_LIST_CREATIVE_SUCCESS, data: nextData, isLast });
  } catch (error) {
    showError(error);
    yield put({ type: types.REQUEST_GET_LIST_CREATIVE_FAILED, error });
  }
}

function* deleteCreative(data) {
  const { listOfId, resetCheckList, toggle, setLoading } = data;
  try {
    const userId = yield select((state) => state.userServletReducer.data.id);
    const listRequest = listOfId.map((id) => CreativeServices.delete({ id, userId }));

    const res = yield Promise.all(listRequest);

    /** Check if fail */
    if (res.some((el) => el.data.message.toLowerCase() === 'ok')) {
      yield refetchListCreative();
    }

    if (res.some((el) => el.data.message.toLowerCase() !== 'ok')) {
      let stopLoop = true;
      res.forEach((i) => {
        if (stopLoop && i.data.message.toLowerCase() !== 'ok') {
          stopLoop = true;
          showError(i.data.message);
          setLoading(false);
          resetCheckList();
          toggle();
        }
      });
      return;
    }

    yield resetCheckList();
    yield toggle();
    yield put({ type: types.REQUEST_DELETE_CREATIVE_SUCCESS });
    Notification('success', 'Delete creative successfully!', 'Success', TIME_OUT_CLOSE_TOAST);
  } catch (error) {
    showError(error);
    setLoading(false);
    yield put({ type: types.REQUEST_DELETE_CREATIVE_FAILED, error });
  }
}

function* createCreative(data) {
  const { title, message, userId, history, callbacks, disableNotice } = data;
  try {
    const res = yield CreativeServices.create({ title, message, userId });

    if (res.data.code && res.data.code === 400) {
      let error = res.data.message;
      !disableNotice && showError(`Title: ${title} - ` + error.toString());

      yield put({ type: types.REQUEST_CREATE_CREATIVE_FAILED, error });
      callbacks && callbacks.onFailed(error);
      return;
    }

    yield refetchListCreative();
    yield put({ type: types.REQUEST_CREATE_CREATIVE_SUCCESS });
    !disableNotice &&
      Notification('success', 'Create creative successfully!', 'Success', TIME_OUT_CLOSE_TOAST);
    history && history.push(AppRoutes.CreativeCompose);
    callbacks && callbacks.onSuccess(res);
  } catch (error) {
    !disableNotice && showError(error);
    yield put({ type: types.REQUEST_CREATE_CREATIVE_FAILED, error });
    callbacks && callbacks.onFailed(error);
  }
}

function* updateCreative(data) {
  const { title, message, id } = data;
  try {
    const userId = yield select((state) => state.userServletReducer.data.id);
    const res = yield CreativeServices.update({ id, userId, title, message });

    const listCreative = yield select((state) => state.creativeReducer.listCreative);
    const cloneCreatives = cloneDeep(listCreative);
    const index = listCreative.findIndex((el) => el.id.toString() === id.toString());

    /** Update new data */
    if (~index) {
      cloneCreatives[index] = { ...res.data };
    }

    yield put({
      type: types.REQUEST_UPDATE_CREATIVE_SUCCESS,
      listCreative: cloneCreatives,
      creative: res.data,
    });
    Notification('success', 'Update creative successfully!', 'Success', TIME_OUT_CLOSE_TOAST);
  } catch (error) {
    showError(error);
    yield put({ type: types.REQUEST_UPDATE_CREATIVE_FAILED, error });
  }
}

function* filterCreative(data) {
  let { resetForm } = data;
  try {
    yield put({ type: types.REQUEST_FILTER_CREATIVE });
    const filters = yield select((state) => state.filterReducer.filterCreative);
    const res = yield CreativeServices.getList(filters);

    const isLast = res.data.data.length < pageSize;
    let listCreative;
    if (filters.offset != 0) {
      listCreative = yield select((state) => state.creativeReducer.listCreative);
      listCreative = uniqBy([...listCreative, ...res.data.data], 'id');
    } else listCreative = res.data.data;

    if (resetForm) {
      yield resetForm({
        messageFilter: '',
        titleFilter: '',
      });
    }

    yield put({
      type: types.REQUEST_FILTER_CREATIVE_SUCCESS,
      listCreative,
      isLast,
    });
  } catch (error) {
    showError(error);
    yield put({ type: types.REQUEST_FILTER_CREATIVE_FAILED, error });
  }
}

function* updateCreativeInCampaign(data) {
  const { id, status, cpId, callbacks } = data;
  try {
    const userId = yield select((state) => state.userServletReducer.data.id);
    const res = yield CreativeServices.updateCreativeInCampaign({ id, status, userId });

    if (res.data.code && res.data.code >= 300) {
      const message = res.data.message;
      showError(message);
      yield put({ type: types.REQUEST_UPDATE_CREATIVE_CAMPAIGN_FAILED, message, id });
      callbacks && callbacks.onFailed();
    } else {
      Notification(
        'success',
        'Change status creative successfully!',
        'Success',
        TIME_OUT_CLOSE_TOAST
      );
      yield put({
        type: types.REQUEST_UPDATE_CREATIVE_CAMPAIGN_SUCCESS,
        cpId,
        id,
        creative: res.data,
      });
      callbacks && callbacks.onSuccess();
    }
  } catch (error) {
    const { body } = error.response.data;
    const { message } = JSON.parse(body);
    callbacks && callbacks.onFailed();
    showError(message);
    yield put({ type: types.REQUEST_UPDATE_CREATIVE_CAMPAIGN_FAILED, error, id });
  }
}

function* uploadAttachments(data) {
  const { body, callbacks } = data;
  const { files, creativeId } = body || {};

  try {
    const response = yield CreativeServices.uploadAttachments({
      files,
      creativeId,
    });

    if (response?.data?.message) {
      yield getCreativeById({ id: creativeId });
      notifySuccess(response?.data?.message || '');
      callbacks && callbacks.onSuccess(response);
    }
  } catch (error) {
    showError(error);
    callbacks && callbacks.onFailed(error);
  }
}

function* deleteAttachment(data) {
  const { body, callbacks, shouldRefetchCreative = true } = data;
  const { attachmentId, creativeId } = body || {};
  try {
    const response = yield CreativeServices.deleteAttachment({
      attachmentId,
    });

    if (response?.data?.message) {
      if (shouldRefetchCreative) {
        yield getCreativeById({ id: creativeId });
        notifySuccess(response?.data?.message || '');
      }
      callbacks && callbacks.onSuccess(response);
    }
  } catch (error) {
    showError(error);
    callbacks && callbacks.onFailed(error);
  }
}

export default function* watchAPIFunction() {
  yield takeLatest(types.REQUEST_UPDATE_CREATIVE, updateCreative);
  yield takeLatest(types.REQUEST_GET_LIST_CREATIVE, getListCreative);
  yield takeLatest(types.REQUEST_GET_CREATIVE_BY_ID, getCreativeById);
  yield takeEvery(types.REQUEST_CREATE_CREATIVE, createCreative);
  yield takeLatest(types.REQUEST_DELETE_CREATIVE, deleteCreative);
  yield takeLatest(types.CHANGE_FILTER_CREATIVE, filterCreative);
  yield takeEvery(types.REQUEST_UPDATE_CREATIVE_CAMPAIGN, updateCreativeInCampaign);
  yield takeLatest(types.REQUEST_UPLOAD_ATTACHMENTS_CREATIVE, uploadAttachments);
  yield takeLatest(types.REQUEST_DELETE_ATTACHMENT, deleteAttachment);
}
